import { useEffect, useState } from 'react';
/**
 * `CustomerCreatePlanPage` renders the customer plan configuration step.
 *
 * Responsibilities:
 * - Displays `ProductSelection` forms for selected products.
 * - Applies default variant/attribute selections when only one option exists.
 * - Coordinates validation across all selected products for checkout and
 *   save-quote flows.
 * - Supports saving/updating quotes via `QuotesApi.saveQuote` and a
 *   confirmation modal (`SaveQuoteConfirmation`).
 * - Prompts for missing VRN via `VrnInputModal` when required.
 * - Applies special policy start date rules for warranty/service-plan products
 *   (including manufacturer warranty expiry logic).
 */
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductSelection, {
  ProductFormStatus
} from '../../common/Product/ProductSelection';
import {
  ProductType,
  QuoteApiResponse,
  QuoteRequest,
  SelectedLinkedProduct
} from '../../common/CommonTypes';
import PriceTotal from '../PriceTotal';
import '../CreatePlanPage.scss';
import {
  PageAbsolutePath,
  ProductTypes,
  QuoteType,
  ProductChannel,
  PageRelativePath,
  VehicleIdType
} from '../../../consts/Common';
import {
  getDefaultClaimLimit,
  getDefaultCoverLevel,
  getDefaultPaymentMethod,
  getDefaultDuration,
  getDefaultVariant,
  getSelectedProducts,
  getSelectedVariant,
  getUniqueCoverLevels,
  getUniqueTyreTypes
} from '../../common/ProductMethods';
import PageHeader from '../../common/PageHeader';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useJourney } from '../Layout';
import VrnInputModal from '../../common/VrnInputModal';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import SaveQuoteConfirmation from '../../common/SaveQuoteConfirmation';
import {
  getSelectedNoOfInstallments,
  getQuotesData,
  getJsonDateString
} from '../../common/HelperMethods';
import { QuotesApi } from '../../../api/QuotesApi';
import { useTranslation } from 'react-i18next';
import { LocationVehicleId } from './CustomerVehicleDetailsPage';
import { PaymentMethod } from '../../common/Constants';
import { getDateByBrowserLanguage } from '../../common/Formatter';
import ClientDateHandler from '../../common/ClientDateHandler/ClientDateHandler';
import { Helmet } from 'react-helmet';
import { useAppSelector } from '../../../app-state/Hooks';
import React from 'react';
import { useCurrencySymbol } from '../../../hooks/useCurrencySymbol';

const PolicyStartDateRule = {
  Existing: 0,
  IgnoreOem: 1,
  OemThenOpen: 2
} as const;

const CustomerCreatePlanPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [priceInFocus, setPriceInFocus] = useState(false);
  const [showVrnModal, setShowVrnModal] = useState<boolean>(false);
  const [disableSaveQuote, setDisableSaveQuote] = useState(false);
  const [validationResults, setValidationResults] = useState<{
    [key: number]: boolean;
  }>({});
  const [status, setStatus] = useState<ProductFormStatus>(
    ProductFormStatus.Idle
  );
  const [showModal, setShowModal] = useState<boolean>(false);
  const { t } = useTranslation();
  const {
    journey,
    onUpdateProducts,
    onPageChange,
    onUpdateVehicleVrn,
    setPersonalDetails,
    onUpdateQuotedProducts
  } = useJourney();

  const [selectedLinkedProducts, setSelectedLinkedProducts] = useState<
    SelectedLinkedProduct[]
  >([]);

  const user = useAppSelector((s) => s.user);
  const appConfig = useAppSelector((s) => s.appConfig);
  const useVinInput = useAppSelector((s) => s.content.landingPage.useVinInput);
  const cms = useAppSelector((s) => s.content.productVariantPage);
  const currencySymbol = useCurrencySymbol();
  const {
    products,
    vehicle,
    quoteProducts,
    quoteId,
    personalDetail,
    dealerCode,
    exceedsUpsellMileageLimit,
    expiryDetails,
    manufacturingCoverageMonths
  } = journey;
  const { vehicleDeliveryDate, vehicleDetails } = vehicle;
  const { vrn, registrationDate } = vehicleDetails;

  const selectedProducts = getSelectedProducts(products);

  const token = journey.token || user.token;

  const isManufacturerWarrantyProduct = (productTypeId: number): boolean => {
    return (
      productTypeId === ProductTypes.Warranty ||
      productTypeId === ProductTypes.RoadsideAssistance
    );
  };

  const getExceptionPolicyStartDateLimit = (): number => {
    return Math.max(0, cms.policyStartDateExceptionLimitDays ?? 0);
  };

  const isVehicleWithinOemWarranty = (): boolean => {
    if (getDefaultStartDate()) {
      return true;
    }

    if (!expiryDetails) {
      return false;
    }

    const today = new Date(new Date().setHours(0, 0, 0, 0));
    const cdh = new ClientDateHandler();
    const expiryDate = cdh.getUtcDate(expiryDetails.oemWarrantyExpiryDate);

    return expiryDate >= today;
  };

  const shouldUseOemPolicyStartDateRule = (productTypeId: number): boolean => {
    if (!isManufacturerWarrantyProduct(productTypeId)) {
      return false;
    }

    if (cms.policyStartDateRule === PolicyStartDateRule.IgnoreOem) {
      return false;
    }

    if (cms.policyStartDateRule === PolicyStartDateRule.OemThenOpen) {
      return isVehicleWithinOemWarranty();
    }

    return true;
  };

  const getMaxPolicyStartDateOffset = (
    product: ProductType
  ): number | undefined => {
    if (product.productTypeId === ProductTypes.ServicePlan) {
      return product.policyStartDateLimit;
    }

    if (!isManufacturerWarrantyProduct(product.productTypeId)) {
      return undefined;
    }

    if (shouldUseOemPolicyStartDateRule(product.productTypeId)) {
      return undefined;
    }

    return getExceptionPolicyStartDateLimit();
  };

  const noOfInstallments = Math.max(
    ...selectedProducts
      .map((p) => p.productVariants)
      .reduce((a, b) => a.concat(b))
      .map((v) => v.noOfInstallments),
    0
  );

  const { state } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    setProductDefaultSelection();

    if (products && products.length > 0) {
      const slp: SelectedLinkedProduct[] = products
        .map((q) => q.selectedLinkedProducts || [])
        .reduce((a, b) => a.concat(b), []);

      setSelectedLinkedProducts(slp);
    }
  }, []);

  useEffect(() => {
    setSaveQuoteButtonState();
  }, [products, status, quoteProducts]);

  useEffect(() => {
    const notAllProductsHaveBeenValidated = selectedProducts.some(
      (p) => validationResults[p.productTypeId] === undefined
    );

    if (notAllProductsHaveBeenValidated) {
      return;
    }

    const allValid = selectedProducts.every(
      (p) => validationResults[p.productTypeId]
    );

    if (!allValid) {
      setStatus(ProductFormStatus.Idle);
      return;
    }

    switch (status) {
      case ProductFormStatus.ValidatingCheckout: {
        const vehicleIdState = state as LocationVehicleId;
        if (vehicleIdState?.vehicleIdType === VehicleIdType.Vin) {
          navigate(PageRelativePath.PersonalDetails, {
            state: {
              vehicleIdType: (state as LocationVehicleId)?.vehicleIdType
            }
          });
          onPageChange(PageAbsolutePath.Customer.Products, true);
        } else {
          if (CommonValidation.isEmpty(vrn) && !useVinInput) {
            setShowVrnModal(true);
          } else {
            navigate(PageRelativePath.PersonalDetails, {
              state: {
                vehicleIdType: (state as LocationVehicleId)?.vehicleIdType
              }
            });
            onPageChange(PageAbsolutePath.Customer.Products, true);
          }
        }
        break;
      }

      case ProductFormStatus.ValidatingSaveQuote: {
        quoteId ? saveQuote() : setShowModal(true);
      }
    }
  }, [validationResults]);

  const saveQuote = () => {
    const marketingPreferences = {
      doNotContact: false
    };

    const controller = new AbortController();

    const successCallback = (data: QuoteApiResponse) => {
      if (data.isSaved) {
        toast.success(
          t(
            'customer-product-page.save-quote-confirmation.quote-save-successmsg'
          ),
          {
            onClose: () => {
              data.quoteDto?.quoteProducts &&
                onUpdateQuotedProducts(data.quoteDto?.quoteProducts);
            },
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored'
          }
        );
      } else {
        toast.error(
          t(
            'customer-product-page.save-quote-confirmation.quote-save-failuremsg'
          ),
          {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored'
          }
        );
      }

      setStatus(ProductFormStatus.Idle);
    };

    const failureCallback = () => {
      console.log('Error occured in quote api save...');
      setStatus(ProductFormStatus.Idle);
    };
    const quotesData = getQuotesData(
      products,
      vehicle,
      user,
      quoteId,
      personalDetail.email,
      personalDetail.title,
      personalDetail.surname,
      marketingPreferences,
      quoteProducts,
      QuoteType.Customer,
      ProductChannel.OpenSite.toString(),
      dealerCode
    );

    const quoteRequest: QuoteRequest = {
      quote: quotesData,
      countryCode: appConfig.countryCode
    };

    new QuotesApi().saveQuote(
      quoteRequest,
      token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const setSaveQuoteButtonState = () => {
    const isQuoteSame = selectedProducts.every((p) => {
      if (p.isPurchased) return true;

      const selectedVariant = getSelectedVariant(p.productVariants);

      if (!selectedVariant) return true;

      const quotedProduct = quoteProducts.find(
        (qp) => qp.productTypeId === p.productTypeId
      );

      if (!quotedProduct) return false;

      return (
        quotedProduct.variantId === selectedVariant.variantId &&
        quotedProduct.quotedPrice === selectedVariant.price
      );
    });

    setDisableSaveQuote(isQuoteSame || status !== ProductFormStatus.Idle);
  };

  const setProductDefaultSelection = () => {
    let updatedProducts: ProductType[] = products;

    selectedProducts.forEach((p) => {
      const selectedVariant = getSelectedVariant(p.productVariants);

      if (!selectedVariant) {
        const tyreTypes = getUniqueTyreTypes(p.productVariants);

        if (tyreTypes.length === 1) {
          const defaultTyreTypeId = tyreTypes[0];
          const defaultClaimLimit = getDefaultClaimLimit(
            p.productVariants,
            defaultTyreTypeId,
            currencySymbol
          );
          const defaultPaymentMethod = getDefaultPaymentMethod(
            p.productVariants,
            defaultTyreTypeId,
            defaultClaimLimit ? defaultClaimLimit.value : undefined,
            p.selectedCoverLevelId
          );
          const defaultDuration = getDefaultDuration(
            p.productVariants,
            defaultTyreTypeId,
            defaultClaimLimit ? defaultClaimLimit.value : undefined,
            p.selectedCoverLevelId,
            defaultPaymentMethod
          );
          const defaultVariant = getDefaultVariant(
            p.productVariants,
            defaultTyreTypeId,
            defaultClaimLimit ? defaultClaimLimit.value : undefined,
            p.selectedCoverLevelId,
            defaultPaymentMethod,
            defaultDuration,
            undefined
          );
          updatedProducts = getUpdatedProducts(
            updatedProducts,
            p.productTypeId,
            defaultTyreTypeId,
            defaultClaimLimit ? defaultClaimLimit.value : undefined,
            p.selectedCoverLevelId,
            p.selectedExcess,
            defaultPaymentMethod,
            defaultVariant?.variantId || ''
          );
        } else {
          const coverLevels = getUniqueCoverLevels(p.productVariants);

          if (coverLevels.length === 1) {
            const defaultCoverLevel = getDefaultCoverLevel(p.productVariants);
            const defaultPaymentMethod = getDefaultPaymentMethod(
              p.productVariants,
              p.selectedTyreTypeId,
              p.selectedClaimLimit,
              defaultCoverLevel?.value
            );
            const defaultDuration = getDefaultDuration(
              p.productVariants,
              p.selectedTyreTypeId,
              p.selectedClaimLimit,
              defaultCoverLevel?.value,
              defaultPaymentMethod
            );
            const defaultVariant = getDefaultVariant(
              p.productVariants,
              p.selectedTyreTypeId,
              p.selectedClaimLimit,
              defaultCoverLevel?.value,
              defaultPaymentMethod,
              defaultDuration,
              undefined
            );
            updatedProducts = getUpdatedProducts(
              updatedProducts,
              p.productTypeId,
              p.selectedTyreTypeId,
              p.selectedClaimLimit,
              defaultCoverLevel?.value,
              p.selectedExcess,
              defaultPaymentMethod,
              defaultVariant?.variantId || ''
            );
          }
        }
      } else if (
        (p.productTypeId === ProductTypes.Tyre && !p.selectedTyreTypeId) ||
        (p.productTypeId !== ProductTypes.Tyre && !p.selectedCoverLevelId)
      ) {
        updatedProducts = getUpdatedProducts(
          updatedProducts,
          p.productTypeId,
          selectedVariant.tyreTypeId,
          selectedVariant.claimLimit,
          selectedVariant.coverLevelId,
          selectedVariant.excess,
          selectedVariant.paymentMethod,
          selectedVariant.variantId
        );
      }
    });
    updatedProducts.forEach((p) => {
      p.selectedCoverLevelId =
        p.productTypeId === ProductTypes.Tyre
          ? undefined
          : p.selectedCoverLevelId;

      if (isManufacturerWarrantyProduct(p.productTypeId)) {
        const shouldUseOemRule = shouldUseOemPolicyStartDateRule(
          p.productTypeId
        );
        const defaultStartDate = shouldUseOemRule ? getDefaultStartDate() : '';
        const isPolicyDateSet =
          p.policyStartDate &&
          !p.policyStartDate.toLowerCase().includes('null');

        p.readOnlyPolicyStartDate = shouldUseOemRule && defaultStartDate !== '';

        if (defaultStartDate && !isPolicyDateSet) {
          p.policyStartDate = defaultStartDate;
        }

        if (!defaultStartDate && expiryDetails && shouldUseOemRule) {
          const today = new Date(new Date().setHours(0, 0, 0, 0));
          const cdh = new ClientDateHandler();
          const expiryDate = cdh.getUtcDate(
            expiryDetails.oemWarrantyExpiryDate
          );
          if (expiryDate < today) {
            p.policyStartDate = getJsonDateString(new Date().toISOString());
          } else if (expiryDate > today) {
            const dayAfterExpiry = new Date(
              expiryDate.setDate(expiryDate.getDate() + 1)
            );
            p.policyStartDate = getJsonDateString(dayAfterExpiry.toISOString());
          } else if (expiryDate.toDateString() === today.toDateString()) {
            const tomorrow = new Date(today.setDate(today.getDate() + 1));
            p.policyStartDate = getJsonDateString(tomorrow.toISOString());
          }

          p.readOnlyPolicyStartDate = true;
        }
      }

      if (
        p.selectedPaymentMethod === PaymentMethod.PaymentAssist &&
        p.productTypeId === ProductTypes.ServicePlan
      ) {
        p.policyStartDate = getJsonDateString(new Date().toISOString());
        p.readOnlyPolicyStartDate = true;
      }
    });
    onUpdateProducts(updatedProducts);
  };

  const getDefaultStartDate = (): string => {
    if (
      manufacturingCoverageMonths &&
      manufacturingCoverageMonths > 0 &&
      registrationDate
    ) {
      // Hardcoded culture because registration date is always in dd/MM/yyyy format
      const firstRegistrationDate = getDateByBrowserLanguage(
        'en-GB',
        registrationDate
      );

      if (!firstRegistrationDate) {
        return '';
      }

      const policyStartMonth: number =
        firstRegistrationDate.getMonth() + manufacturingCoverageMonths;
      const policyStartDay: number = firstRegistrationDate.getDate();

      firstRegistrationDate.setMonth(policyStartMonth);
      firstRegistrationDate.setDate(policyStartDay);

      //Convert to UTC to factor in time zone differences, daylight saving, etc.
      const defaultStartDate = new Date(
        Date.UTC(
          firstRegistrationDate.getFullYear(),
          firstRegistrationDate.getMonth(),
          firstRegistrationDate.getDate()
        )
      );

      if (!defaultStartDate) {
        return '';
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const isDateInPast = defaultStartDate < today;

      if (isDateInPast) {
        return '';
      }

      const jsonDefaultStartDate = getJsonDateString(
        defaultStartDate.toISOString()
      );

      return jsonDefaultStartDate;
    }

    return '';
  };

  const addOrRemoveProduct = (
    productTypeCode: string,
    productTypeId: number
  ) => {
    const quotedProduct = quoteProducts.find(
      (qp) => qp.productTypeId === productTypeId
    );
    const quotedVariantId = quotedProduct?.variantId ?? '';
    const updatedProducts = products.map((product) =>
      product.productTypeCode === productTypeCode
        ? {
            ...product,
            isSelected: !product.isSelected,
            productVariants: product.productVariants.map((variant) => {
              return {
                ...variant,
                isSelected: variant.variantId === quotedVariantId
              };
            })
          }
        : product
    );
    onUpdateProducts(updatedProducts, true);
    const selectedUpdatedProducts = getSelectedProducts(updatedProducts);
    if (selectedUpdatedProducts.length === 0) {
      onBack();
    }
  };

  const onChangePolicyStartDate = (productTypeId: number, dateVal: string) => {
    const updatedProducts = products.map((product) =>
      product.productTypeId === productTypeId
        ? { ...product, policyStartDate: dateVal }
        : product
    );

    onUpdateProducts(updatedProducts);
  };

  const onProductSelectionChange = (
    productTypeId: number,
    tyreTypeId: number | undefined,
    claimLimit: number | undefined,
    coverLevelId: number | undefined,
    excess: number | undefined,
    paymentMethod: string | undefined,
    variantId: string
  ) => {
    const updatedProducts = getUpdatedProducts(
      products,
      productTypeId,
      tyreTypeId,
      claimLimit,
      coverLevelId,
      excess,
      paymentMethod,
      variantId
    );
    onUpdateProducts(updatedProducts);
  };

  const getUpdatedProducts = (
    initialProducts: ProductType[],
    productTypeId: number,
    tyreTypeId: number | undefined,
    claimLimit: number | undefined,
    coverLevelId: number | undefined,
    excess: number | undefined,
    paymentMethod: string | undefined,
    variantId: string
  ) => {
    const updatedProducts = initialProducts.map((product) =>
      product.productTypeId === productTypeId
        ? {
            ...product,
            selectedTyreTypeId: tyreTypeId,
            selectedClaimLimit: claimLimit,
            selectedCoverLevelId: coverLevelId,
            selectedPaymentMethod: paymentMethod,
            selectedExcess: excess,
            productVariants: product.productVariants.map((variant) => ({
              ...variant,
              isSelected: variant.variantId === variantId
            }))
          }
        : product
    );

    return updatedProducts;
  };

  const onUpdateVariantPrice = (
    productTypeId: number,
    priceValue: number | undefined
  ) => {
    const updatedProducts = products.map((product) =>
      product.productTypeId === productTypeId
        ? {
            ...product,
            productVariants: product.productVariants.map((variant) =>
              variant.isSelected
                ? {
                    ...variant,
                    price: priceValue ?? 0
                  }
                : variant
            )
          }
        : product
    );

    onUpdateProducts(updatedProducts);
    setPriceInFocus(false);
  };

  const onBack = () => {
    navigate(PageRelativePath.Products, {
      state: {
        expiryDetails,
        vehicleIdType: (state as LocationVehicleId)?.vehicleIdType
      }
    });
    onPageChange(PageAbsolutePath.Customer.Products, true);
  };

  const onCheckout = () => {
    setValidationResults({});
    setStatus(ProductFormStatus.ValidatingCheckout);
  };

  const onSaveQuote = () => {
    setValidationResults({});
    setStatus(ProductFormStatus.ValidatingSaveQuote);
  };

  const onValidated = (productTypeId: number, isValid: boolean) => {
    setValidationResults((vr) => {
      return { ...vr, ...{ [productTypeId]: isValid } };
    });
  };

  const onPriceFocus = () => {
    setPriceInFocus(true);
  };

  const informationText = cms.information
    ? cms.information.replace('{NoOfInstalments}', noOfInstallments.toString())
    : '';

  const onCloseVrnModal = () => {
    setShowVrnModal(false);
    setStatus(ProductFormStatus.Idle);
  };

  const onVrnUpdate = (inputVrn: string): void => {
    onUpdateVehicleVrn(inputVrn);
    onCloseVrnModal();
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const closeModalOnConfirm = () => {
    setShowModal(false);
    setDisableSaveQuote(true);
    setStatus(ProductFormStatus.Idle);
  };

  const getManufacturerWarrantyRenewDate = (productTypeId: number) => {
    return shouldUseOemPolicyStartDateRule(productTypeId)
      ? getDefaultStartDate()
      : '';
  };

  return (
    <div>
      <Helmet>
        <title>{t('page-title.customer-plan')}</title>
      </Helmet>
      <PageHeader
        title={cms.title}
        description={cms.description}
        information={informationText}
        warning={cms.warning}
        descriptionClass='p2'
      />
      <div className='flex-row'>
        <div className='flex-column product-selection-card-container'>
          {selectedProducts.map((product: ProductType) => (
            <ProductSelection
              key={`selection-${product.productTypeId}`}
              product={product}
              noOfInstallments={getSelectedNoOfInstallments(product)}
              paymentOptions={false}
              isQuote={quoteId > 0 ? true : false}
              addOrRemoveProduct={addOrRemoveProduct}
              onChangePolicyStartDate={onChangePolicyStartDate}
              onProductSelectionChange={onProductSelectionChange}
              onUpdateVariantPrice={onUpdateVariantPrice}
              onPriceFocus={onPriceFocus}
              vehicleDeliveryDate={vehicleDeliveryDate ?? ''}
              showPriceField={false}
              changeTotalPriceOnVariantChange={true}
              productFormStatus={status}
              onValidated={onValidated}
              exceedsUpsellMileageLimit={exceedsUpsellMileageLimit}
              culture={appConfig.cultureCode}
              hideRemovePlanLink={selectedProducts.length === 1}
              paymentProfileLabel={cms.paymentProfileLabel}
              manufacturersWarrantyRenewDate={getManufacturerWarrantyRenewDate(
                product.productTypeId
              )}
              maxPolicyStartDateOffset={getMaxPolicyStartDateOffset(product)}
              selectedLinkedProducts={selectedLinkedProducts}
            />
          ))}
          <PriceTotal products={products} culture={appConfig.cultureCode} />
        </div>
      </div>
      <div className='flex-row form-nav navigation-desktop-hide text-align-center'>
        {appConfig.includeQuotes && (
          <ButtonContinue
            name='btn-save-products'
            text={quoteId ? 'Save Changes' : 'Save Quote'}
            onClick={onSaveQuote}
            disabled={disableSaveQuote}
          />
        )}
      </div>
      <div className='flex-row form-nav'>
        <ButtonBack
          name='btnBack'
          text={t('customer-button.back-buttont-text')}
          onClick={onBack}
        />
        <div>
          {appConfig.includeQuotes && (
            <ButtonContinue
              name='btn-save-products'
              text={
                quoteId
                  ? t('customer-button.button-save-changes-text')
                  : t('customer-button.button-save-quote')
              }
              onClick={onSaveQuote}
              disabled={disableSaveQuote}
              additionalClassNames='navigation-mobile-hide'
            />
          )}
          <SaveQuoteConfirmation
            showModal={showModal}
            onCloseModal={onCloseModal}
            closeModalOnConfirm={closeModalOnConfirm}
            products={products}
            vehicle={vehicle}
            quoteId={quoteId}
            personalDetails={personalDetail}
            setPersonalDetails={setPersonalDetails}
            quoteType={QuoteType.Customer}
            channels={ProductChannel.OpenSite}
          />
          <ButtonContinue
            name='btn-see-products'
            text={t('customer-button.button-checkout-text')}
            onClick={onCheckout}
            disabled={priceInFocus}
            additionalClassNames='m05-left'
          />
        </div>
      </div>
      {showVrnModal && (
        <VrnInputModal
          vrn={vrn}
          onUpdateVrn={onVrnUpdate}
          showModal={showVrnModal}
          onCloseModal={onCloseVrnModal}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default CustomerCreatePlanPage;
